import React from 'react';
/**
 * `UsefulDocuments` renders a tree view of available documents.
 *
 * Behaviour:
 * - Renders CMS documents as leaf nodes (always shown) and product documents as
 *   a nested tree (filtered by each node's `show` flag).
 * - Uses `expandedDocIds` + `setExpandedDocIds` to control expand/collapse
 *   state.
 * - Uses `DocTreeItem` to handle document link opening.
 * - Hides the entire panel when there are no visible documents.
 */
import { DocumentItem, DocumentTreeItem } from '../common/CommonTypes';
import DocTreeItem from './DocTreeItem';
import { SimpleTreeView } from '@mui/x-tree-view';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import './UsefulDocuments.scss';
import { useTranslation } from 'react-i18next';

export type UsefulDocumentProps = {
  documents: DocumentTreeItem[];
  cmsDocuments?: DocumentItem[];
  view: string;
  expandedDocIds: string[];
  setExpandedDocIds: (docIds: string[]) => void;
  showDivider: boolean;
};

const UsefulDocuments = ({
  documents,
  cmsDocuments,
  view,
  expandedDocIds,
  setExpandedDocIds,
  showDivider
}: UsefulDocumentProps): JSX.Element => {
  const { t } = useTranslation();

  const onExpandCollapse = (event: React.SyntheticEvent, itemIds: string[]) => {
    setExpandedDocIds(itemIds);
  };

  const renderTreeNode = (node: DocumentTreeItem): JSX.Element => {
    return (
      <React.Fragment key={`${view}-document-${node.id}`}>
        {node.show && (
          <DocTreeItem item={node}>
            {Array.isArray(node.children)
              ? node.children.map((n) => renderTreeNode(n))
              : null}
          </DocTreeItem>
        )}
      </React.Fragment>
    );
  };

  const renderCmsDocument = (
    document: DocumentItem,
    index: number
  ): JSX.Element => {
    const item: DocumentTreeItem = {
      id: `${view}-cmsdocument-${index}`,
      name: document.documentName,
      path: document.documentPath,
      level: 0,
      show: true,
      showIcon: true
    };

    return <DocTreeItem key={item.id} item={item}></DocTreeItem>;
  };

  const hasDocuments =
    documents.some((d) => d.show === true) ||
    (cmsDocuments && cmsDocuments.length > 0);

  if (!hasDocuments) {
    return <></>;
  }

  return (
    <div className='info'>
      {showDivider && <hr />}
      <div className={showDivider ? 'm05-bottom' : 'm05-bottom m1-top'}>
        <p className='p1-bold m05-bottom'>
          {t('page-info.useful-documents-label')}
        </p>
        <SimpleTreeView
          aria-label='rich object'
          slots={{
            collapseIcon: ExpandMoreIcon,
            expandIcon: ChevronRightIcon
          }}
          defaultExpandedItems={expandedDocIds || []}
          onExpandedItemsChange={onExpandCollapse}
          className='cms-document-link'
          sx={{
            flexGrow: 1,
            maxWidth: 400,
            overflowY: 'auto'
          }}>
          <>
            {cmsDocuments?.map((d, i) => renderCmsDocument(d, i))}
            {documents.map((d) => renderTreeNode(d))}
          </>
        </SimpleTreeView>
      </div>
    </div>
  );
};

export default UsefulDocuments;
